import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from 'tss-react/mui';
import { useRoute } from 'wouter';

import Link from '../../components/Link';
import { useSubNavCriteria } from '../../contexts/SubNavContext';

const useStyles = makeStyles()((theme, { collapsed, isActive }) => ({
  listItem: {
    padding: '2px 10px',
    display: 'flex',
  },
  link: {
    flex: 1,
    '&:hover': { textDecoration: 'none' },
  },
  button: {
    padding: `6px ${theme.spacing(collapsed ? 0 : 1)}`,
    minHeight: 44,
    justifyContent: collapsed ? 'center' : 'flex-start',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: isActive ? theme.palette.primary.main : undefined,
    '&:hover': {
      backgroundColor: isActive ? theme.palette.primary.main : undefined,
      textDecoration: 'none',
    },
  },
  icon: {
    minWidth: collapsed ? 0 : 36,
    color: isActive
      ? theme.palette.background.default
      : theme.palette.primary.main,
  },
  text: {
    fontSize: 14,
    margin: 0,
    overflowWrap: 'anywhere',
    color: isActive
      ? theme.palette.background.default
      : theme.palette.primary.main,
  },
  subNavIcon: {
    color: isActive
      ? theme.palette.background.default
      : theme.palette.primary.main,
  },
}));

const NavItem = React.memo(({ icon, text, path, isSubNav, collapsed }) => {
  // mark the link as active also for sub-routes
  const [isActive] = useRoute(path + '/*?');
  const { subNavCriteria, setSubNanCriteria } = useSubNavCriteria();
  const { classes } = useStyles({ collapsed, isActive });

  const handleSubNav = () => {
    setSubNanCriteria(!subNavCriteria);
  };

  const handleNavigation = () => {
    // Clear pagination and rowsPerPage sessionStorage when navigating to Members, Claims, or Wallets
    if (path === '/members') {
      sessionStorage.removeItem('members_pagination');
      sessionStorage.removeItem('members_rowsPerPage');
    } else if (path === '/claims') {
      sessionStorage.removeItem('claims_pagination');
      sessionStorage.removeItem('claims_rowsPerPage');
    } else if (path === '/wallets') {
      sessionStorage.removeItem('wallets_pagination');
      sessionStorage.removeItem('wallets_rowsPerPage');
    }
  };

  return (
    <ListItem key={path} className={classes.listItem}>
      <Link
        href={path.toLowerCase()}
        aria-label={text}
        aria-current={isActive ? 'page' : undefined}
        className={classes.link}
        onClick={() => {
          handleNavigation();
          if (isSubNav) {
            handleSubNav();
          }
        }}
      >
        <Tooltip title={collapsed ? text : ''} placement="right">
          <ListItemButton
            component="span"
            role={undefined}
            tabIndex={undefined}
            className={classes.button}
          >
            <>
              <ListItemIcon className={classes.icon}>{icon}</ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={text}
                  disableTypography
                  className={classes.text}
                />
              )}
              {!collapsed && isSubNav && !subNavCriteria && (
                <ExpandMoreIcon className={classes.subNavIcon} />
              )}
              {!collapsed && isSubNav && subNavCriteria && (
                <KeyboardArrowUpIcon className={classes.subNavIcon} />
              )}
            </>
          </ListItemButton>
        </Tooltip>
      </Link>
    </ListItem>
  );
});

NavItem.displayName = 'NavItem';

export default NavItem;
